import { safeAccessibilityRole } from '@/constants/utils/a11y';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { MONO } from '@/components/Landing/landingTokens';
import { BASE_URL, QUICKSTART } from '@/constants/apiContent';
import highlight from '@/constants/utils/highlight';
import { ApiTabs, useApiDesign } from './ApiEditorial';

const SAMPLES = QUICKSTART.slice(0, 3);
const COLORS = { kw: '#C8A5F8', fn: '#A2C7F3', str: '#B7D8C3', num: '#E9CC99', com: '#9898A1', p: '#DCDCE2' };

export default function ApiCodeSample() {
  const { mobile, isDark } = useApiDesign();
  const [activeId, setActiveId] = useState(SAMPLES[0].id);
  const [copyState, setCopyState] = useState('idle');
  const timer = useRef();
  const active = SAMPLES.find(item => item.id === activeId);
  const lines = useMemo(() => highlight(active.code, active.language), [active]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(active.code);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopyState('idle'), 2400);
  };
  const select = id => { setActiveId(id); setCopyState('idle'); clearTimeout(timer.current); };
  return (
    <View style={{ backgroundColor: '#1C1C1F', borderRadius: 22, overflow: 'hidden', borderWidth: 1, borderColor: '#38383D',
      boxShadow: isDark ? '0 24px 64px -24px rgba(0,0,0,0.6)' : '0 24px 64px -24px rgba(0,0,0,0.22)' }}>
      <View style={{ padding: mobile ? 10 : 14, borderBottomWidth: 1, borderBottomColor: '#343438', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
        <ApiTabs id="api-code" label="Code language" items={SAMPLES} activeId={activeId} onChange={select} dark />
        <Pressable onPress={copy} accessibilityRole="button" accessibilityLabel={copyState === 'copied' ? 'Copied code' : 'Copy code'}
          style={({ hovered }) => ({ width: 44, height: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 10, backgroundColor: hovered ? '#36363A' : 'transparent' })}>
          <Icon name={copyState === 'copied' ? 'check' : 'copy'} size={15} color="#C4C4CC" />
        </Pressable>
      </View>
      <View nativeID={`api-code-panel-${active.id}`} accessibilityRole={safeAccessibilityRole('tabpanel')} aria-labelledby={`api-code-tab-${active.id}`} tabIndex={0}
        style={{ padding: mobile ? 18 : 28, minHeight: mobile ? 390 : 410 }}>
        <Text tag="pre" style={{ fontFamily: MONO, fontSize: mobile ? 11.5 : 12.5, lineHeight: 22, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: COLORS.p }}>
          {lines.map((segments, index) => (
            <React.Fragment key={index}>
              {index ? '\n' : null}
              {segments.map((segment, segmentIndex) => <span key={segmentIndex} style={{ color: COLORS[segment.c] || COLORS.p }}>{segment.t}</span>)}
            </React.Fragment>
          ))}
        </Text>
      </View>
      <View style={{ borderTopWidth: 1, borderTopColor: '#343438', paddingHorizontal: mobile ? 18 : 28, paddingVertical: 16, gap: 5 }}>
        <Text style={{ color: '#ABABB4', fontSize: 11, lineHeight: 18, fontFamily: MONO }}>{BASE_URL}</Text>
        <Text accessibilityLiveRegion="polite" style={{ color: '#ABABB4', fontSize: 12, lineHeight: 18 }}>{copyState === 'copied' ? 'Copied to clipboard' : copyState === 'error' ? 'Couldn’t copy. Select the code to copy it manually.' : 'Works with the OpenAI SDK'}</Text>
      </View>
    </View>
  );
}
